import { Grid, Typography } from '@material-ui/core';
import { Switch } from '@ncr-retail/base-retail-components';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BaseButton from 'src/common/components/base-button';
import { setHomePage } from 'src/common/components/emerald-home-page/emerald-home-page';
import { PriceModifierAction } from 'src/common/enumerations/price-modifier-action';
import { IButton } from 'src/common/interfaces/IButton';
import { IElectronicJournalRecord } from 'src/common/interfaces/states/IElectronicJournalRecord';
import { POSModeState } from 'src/common/interfaces/states/POSModeState';
import { store } from 'src/common/redux/configure-store';
import { trainingModeStyles } from 'src/common/training-mode/training-mode-styles';
import Keypad from 'src/keypad/components/keypad';
import { priceFormat } from 'src/keypad/keypad-operation';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import PriceOverrideInput from '../price-override-input';
import { styles } from './price-override-styles';
import { PriceOverrideType } from 'src/common/enumerations/price-override-type';
import { UnitOfMeasureTypes } from 'src/common/enumerations/unit-of-measure-type';
import { truncatePrice } from 'src/common/helpers/number-helper';
import { convertCurrency } from 'src/common/helpers/currency-helper';

type Props = IPriceOverrideProps;

export const showPriceOverrideDialog = (props: Props): void => {
  setHomePage(<PriceOverride {...props} />);
};

const PriceOverride: React.FC<Props> = (props: Props): JSX.Element => {
  const { t } = useTranslation();
  const classes = styles();
  const trainingModeClasses = trainingModeStyles();
  const { priceOverrideType, onSubmit, onCancel } = props;
  const {
    Name,
    ItemId,
    UnitOfMeasureCode,
    IsMsu,
    PackQuantity,
    LinkedLines,
    OverrideUnitPrice,
    RegularPrice,
  } = props.selectedItem;
  const UnitPrice = props.selectedItem.UnitPrice ?? '';
  const unitPriceTokens = props.selectedItem.OverrideUnitPrice?.split('/') ?? UnitPrice.split('/');

  let price = props.selectedItem.NumericUnitPrice ?? 0;
  if (PackQuantity?.unitPrice) {
    price = PackQuantity?.unitPrice;
  } else if (UnitOfMeasureCode === UnitOfMeasureTypes.Kilogram) {
    price = RegularPrice?.unitPrice ?? price;
  } else if (LinkedLines?.length === 0 && RegularPrice?.unitPrice) {
    price = RegularPrice?.unitPrice;
  }

  const inputRef = useRef<HTMLInputElement>(null);
  const isDisplayApplySameItemsInTransactionToggleButton = !PackQuantity;
  const atomicUnitPrice = OverrideUnitPrice
    ? OverrideUnitPrice.split('/')[0].trimEnd()
    : convertCurrency(price);

  let atomicUnit = unitPriceTokens[1];
  const nameOverflowLengthLimit = 61;
  const posModeState: POSModeState = store.getState().posMode;
  if (UnitOfMeasureCode === UnitOfMeasureTypes.Each && IsMsu) {
    atomicUnit = ` ${atomicUnit.split(' ')[1]} ${t('UOMS_')}`;
  } else if (UnitOfMeasureCode === UnitOfMeasureTypes.Libra && IsMsu) {
    atomicUnit = ` ${atomicUnit.split(' ')[1]} ${t('UOM_LBR')}`;
  } else if (PackQuantity) {
    atomicUnit = PackQuantity.units
      ? ` ${t('UOM_Pack')} (${PackQuantity.units} ${atomicUnit})`
      : ` ${t('UOM_Pack')}`;
  } else if (UnitOfMeasureCode === UnitOfMeasureTypes.Each) {
    atomicUnit = ` ${t('UOM_')}`;
  } else if (UnitOfMeasureCode === UnitOfMeasureTypes.Litre) {
    atomicUnit = ` ${t('UOM_LTR')}`;
  } else if (UnitOfMeasureCode === UnitOfMeasureTypes.Libra) {
    atomicUnit = ` ${t('UOM_LBR')}`;
  }

  const [payload, setPayload] = useState<IPriceOverridePayload>({
    action: PriceModifierAction.NewPrice,
    sameSoldChecked: false,
    value: '0.00',
  });

  const handleSubmit = (): void => {
    if (payload.value) {
      if (!Number.isNaN(Number(payload.value))) {
        onSubmit(payload);
      } else {
        popupMessage({ message: 'InvalidInput' });
      }
    } else {
      popupMessage({ message: 'NoInputWasDone' });
    }
  };

  const toggleChecked = (): void => {
    const checked = !payload.sameSoldChecked;
    setPayload(prevPayload => ({ ...prevPayload, sameSoldChecked: checked }));
  };

  const handleOnFocus = (e): void => {
    const targetId = e.target.id;
    if (payload.action !== targetId) {
      setPayload(prevPayload => ({
        ...prevPayload,
        action: targetId,
        value: targetId === PriceModifierAction.Percent ? '' : '0.00',
      }));
    }
  };

  const handleChange = (e): void => {
    const valueWithNumber =
      payload.action !== PriceModifierAction.Percent
        ? priceFormat(e.target.value, inputRef)
        : e.target.value.replace(/[^.0-9]/g, '').replace(/(\..*)\./g, '.');
    const value = truncatePrice(valueWithNumber);
    setPayload(prevPayload => ({ ...prevPayload, value }));
  };

  const clearInput = (): void => {
    setPayload(prevPayload => ({
      ...prevPayload,
      value: prevPayload.action === PriceModifierAction.Percent ? '' : '0.00',
    }));
  };

  const keypadSubmitButton: IButton = {
    label: t('PriceOverride_Override'),
    onClick: handleSubmit,
    testId: 'override-keypad-button',
  };

  const handleKeypress = (event: KeyboardEvent): void => {
    /* [ENTER] = 13, [ESC] = 27 */
    if (event.keyCode === 13) {
      handleSubmit();
    } else if (event.keyCode === 27) {
      onCancel();
    }
  };

  const handleResizingForTextOverflow = (
    itemName: string,
    defaultClass: string,
    overflowClass: string,
  ): string => {
    return itemName.length <= nameOverflowLengthLimit ? defaultClass : overflowClass;
  };

  return (
    <Grid
      item
      className={`fullHeight flexFull ${classes.outerMostContainer}`}
      data-testid='price-override'
    >
      <Grid
        item
        className={`${
          posModeState.trainingMode
            ? trainingModeClasses.trainingModeBackground
            : classes.background
        } fullWidth fullHeight`}
      >
        <Grid
          container
          direction='column'
          className={`fullHeight ${classes.priceOverrideContainer} ${classes.outerFlexDesign}`}
          data-testid={`price-override-container`}
        >
          <Grid
            container
            direction='column'
            justifyContent='center'
            className={`${classes.priceOverrideTitleContainer} fullWidth`}
            data-testid={`price-override-title`}
          >
            <Grid item className={classes.fitHeight}>
              <Grid
                container
                direction='column'
                className={`${classes.priceOverrideTitleContainer} fullWidth`}
              >
                <Typography
                  variant='h2'
                  className={classes.priceOverrideTitleHeader}
                  data-testid={`price-override-title-text`}
                >
                  {t('PriceOverrideCommand')}
                </Typography>
                <Grid item xs={1}>
                  <Typography className={`${classes.priceOverrideTitleBar}`} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item container className={classes.flexDesign}>
            <Grid item container xs={9}>
              <Grid container>
                <Grid item xs={12} className={classes.itemDetailsContainer}>
                  <Grid
                    justifyContent='space-between'
                    alignItems='center'
                    container
                    className={classes.itemDetails}
                  >
                    <Grid item>
                      <Typography
                        className={handleResizingForTextOverflow(
                          Name,
                          classes.item,
                          classes.itemOverflow,
                        )}
                        display='inline'
                        data-testid='price-override-item-name'
                      >
                        {Name}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Typography className={classes.originalPrice}>
                        {t('PriceOverrideScreen_sell_price')}
                      </Typography>
                      <Typography
                        className={classes.itemPrice}
                        data-testid='price-override-unit-price'
                      >
                        {' '}
                        {atomicUnitPrice}
                      </Typography>
                      <Typography className={classes.priceUnit} data-testid='price-override-uom'>
                        {'/'}
                        {`${atomicUnit} ${
                          LinkedLines && LinkedLines?.length > 0 ? t('WithLink') : ''
                        }`}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item>
                      <Typography
                        data-testid='price-override-item-code'
                        className={classes.itemCode}
                      >
                        {t('ItemView_Code')} {ItemId}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              <Grid container justifyContent='center'>
                <Grid item md={10} lg={8}>
                  {Object.keys(PriceModifierAction).map((actionType: string):
                    | JSX.Element
                    | undefined =>
                    actionType === PriceModifierAction.NewPrice ||
                    priceOverrideType === PriceOverrideType.Extended ? (
                      <PriceOverrideInput
                        key={actionType}
                        input={payload.value}
                        action={actionType as PriceModifierAction}
                        focusedAction={payload.action}
                        unit={atomicUnit}
                        onFocus={handleOnFocus}
                        onChange={handleChange}
                        onClear={clearInput}
                        onKeyDown={handleKeypress}
                        priceOverrideType={priceOverrideType}
                        inputRef={inputRef}
                      />
                    ) : (
                      undefined
                    ),
                  )}

                  {isDisplayApplySameItemsInTransactionToggleButton && (
                    <Grid className={`fullWidth`} container>
                      <SameSoldSlider
                        isChecked={payload.sameSoldChecked}
                        toggleChecked={toggleChecked}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid item container justifyContent='center' className={classes.selfAlign}>
                <BaseButton
                  buttonType='white'
                  className={classes.buttonStyles}
                  onClick={onCancel}
                  data-testid='price-override-cancel-button'
                >
                  {t('Cancel')}
                </BaseButton>
              </Grid>
            </Grid>
            <Grid item className={classes.keypadContainer}>
              <Keypad
                visible={true}
                keyboardId={payload.action}
                noInputField={true}
                keypadTestIdPrefix={'price-override-'}
                topLabelText={t('Price_Override_Keyboard')}
                submitButton={keypadSubmitButton}
                disableDecimalInput={payload.value === '' ? true : false}
                replaceDecimalInput={payload.action !== PriceModifierAction.Percent}
                disableManualWeight={true}
                disableQuantity={true}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const SameSoldSlider = ({ isChecked, toggleChecked }): JSX.Element => {
  const { t } = useTranslation();
  const classes = styles();
  return (
    <Grid container className={`fullWidth ${classes.switchContainer}`}>
      <Grid item xs={8}>
        <Typography
          className={`${classes.subHeading} ${
            isChecked ? classes.selectedSubHeading : classes.uncheckedSubHeading
          } -last`}
          variant='h4'
        >
          {t('PriceOverrideScreen_all_items')}
        </Typography>
      </Grid>
      <Grid item data-testid='same-sold-slider' xs={4}>
        <Switch
          checked={isChecked}
          style={{ display: 'inline-block' }}
          data-testid='same-sold-switch'
          onClick={toggleChecked}
        />
      </Grid>
    </Grid>
  );
};

export interface IPriceOverridePayload {
  value: string;
  action: PriceModifierAction;
  sameSoldChecked: boolean;
}

export interface IPriceOverrideProps {
  selectedItem: IElectronicJournalRecord;
  onSubmit: (priceOverride: IPriceOverridePayload) => void;
  onCancel: () => void;
  priceOverrideType: PriceOverrideType;
}

export default React.memo(PriceOverride);
